"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useState } from "react";
export default function FreeQuoteSection() {
  return <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FormProvider className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" formId="free-quote-epc-form" sectionName="free-quote-section" defaultValues={{
        name: "",
        email: "",
        address: "",
        phone: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 50
        },
        email: {
          required: true,
          pattern: "email"
        },
        address: {
          required: true,
          minLength: 5
        },
        phone: {
          required: true,
          pattern: "phone"
        }
      }} submitText="Vraag Gratis Offerte Aan" successMessage="Bedankt! We nemen spoedig contact met u op.">
          <QuoteFormContent />
        </FormProvider>
      </div>
    </section>;
}
function QuoteFormContent() {
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: illustrationRef,
    isVisible: illustrationVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const [scrollY, setScrollY] = useState(0);
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  if (isSubmitted && !submitError) {
    return <div className="lg:col-span-2">
        <div className="p-8 bg-primary-light border border-primary rounded-md shadow-soft text-center">
          <p className="text-lg font-semibold text-foreground mb-4">{successMessage}</p>
          <Button onClick={resetForm} variant="outline">
            Nog een offerte aanvragen
          </Button>
        </div>
      </div>;
  }
  return <>
      <div className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <h2 className="text-3xl font-bold text-foreground mb-2">Gratis Offerte</h2>
        <p className="text-base text-muted-foreground mb-6">
          Vul het formulier in en ontvang een gratis offerte voor uw EPC-keuring.
        </p>
      </div>

      <Form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
        <div className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: formVisible ? "80ms" : "0ms"
      }}>
          <FormField control={control} name="name" rules={{
          required: "Naam is verplicht",
          minLength: {
            value: 2,
            message: "Minimaal 2 karakters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Naam</FormLabel>
                <FormControl>
                  <Input placeholder="Uw volledige naam" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: formVisible ? "160ms" : "0ms"
      }}>
          <FormField control={control} name="email" rules={{
          required: "E-mail is verplicht",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Ongeldig e-mailadres"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>E-mail</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="uw@email.be" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: formVisible ? "240ms" : "0ms"
      }}>
          <FormField control={control} name="address" rules={{
          required: "Adres is verplicht",
          minLength: {
            value: 5,
            message: "Minimaal 5 karakters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Adres van het pand</FormLabel>
                <FormControl>
                  <Input placeholder="Straat 123, 1000 Stad" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: formVisible ? "320ms" : "0ms"
      }}>
          <FormField control={control} name="phone" rules={{
          required: "Telefoonnummer is verplicht",
          pattern: {
            value: /^[\d\s\+\-\(\)]+$/,
            message: "Ongeldig telefoonnummer"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Telefoonnummer</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+32 2 123 45 67" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{submitError}</p>
          </div>}

        <div className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: formVisible ? "400ms" : "0ms"
      }}>
          <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-foreground-primary hover:bg-primary-hover hover:shadow-medium hover:scale-[1.04] active:bg-primary-active active:scale-100 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Verzenden...
              </> : "Vraag Gratis Offerte Aan"}
          </Button>
        </div>
      </Form>

      <div ref={illustrationRef} className={`flex items-center justify-center transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${illustrationVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
      transform: illustrationVisible ? `translateY(-${scrollY * 0.1}px)` : "translateY(1rem)"
    }}>
        <div className="relative w-full max-w-md">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1511866/img/friendly_inspector_with_clipboard.png" alt="Vriendelijke inspecteur met klembord" className="w-full h-auto" />
        </div>
      </div>
    </>;
}